/*
 * ONC-135: embalagem de presente por produto no carrinho.
 * - m-gift-wrap-button: link "Adicionar embalagem para presente" de cada produto.
 * - m-gift-wrap-sheet: painel de escolha; adiciona via /cart/add.js.
 * - m-gift-wrap-sync: corrige a quantidade de embalagens via /cart/update.js.
 * A embalagem guarda o variant_id do produto em _gift_wrap_for. Depois de cada
 * chamada o carrinho e redesenhado pelo evento cartUpdate do Minimog.
 */

function getGiftWrapSectionsToBundle() {
  const sections = [];
  document.documentElement.dispatchEvent(
    new CustomEvent("cart:grouped-sections", { bubbles: true, detail: { sections } })
  );
  return sections;
}

function emitGiftWrapCartUpdate(cart) {
  window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, { cart });
}

if (!customElements.get("m-gift-wrap-button")) {
  class MGiftWrapButton extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector("button");
      this.onClick = this.onClick.bind(this);
      if (this.button) this.button.addEventListener("click", this.onClick);
    }

    disconnectedCallback() {
      if (this.button) this.button.removeEventListener("click", this.onClick);
    }

    onClick(event) {
      event.preventDefault();
      const root = this.closest("m-cart-drawer, m-cart");
      const sheet = root && root.querySelector("m-gift-wrap-sheet");
      if (!sheet) return;

      sheet.open({
        variantId: this.dataset.variantId,
        itemTitle: this.dataset.itemTitle,
        trigger: this.button,
      });
    }
  }
  customElements.define("m-gift-wrap-button", MGiftWrapButton);
}

if (!customElements.get("m-gift-wrap-sheet")) {
  class MGiftWrapSheet extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector(".m-gift-wrap-sheet__panel");
      this.forLabel = this.querySelector("[data-gift-wrap-for]");
      this.errorEl = this.querySelector(".m-gift-wrap-sheet__error");
      this.isDrawer = this.classList.contains("m-gift-wrap-sheet--drawer");
      this.busy = false;

      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.addEventListener("click", this.onClick);
      this.addEventListener("keydown", this.onKeydown);
    }

    disconnectedCallback() {
      this.removeEventListener("click", this.onClick);
      this.removeEventListener("keydown", this.onKeydown);
      if (this.isOpen && !this.isDrawer) document.documentElement.classList.remove("prevent-scroll");
    }

    get isOpen() {
      return this.classList.contains("is-open");
    }

    open({ variantId, itemTitle, trigger }) {
      this.variantId = variantId;
      this.itemTitle = itemTitle || "";
      this.trigger = trigger;

      if (this.forLabel) this.forLabel.textContent = this.dataset.forTemplate.replace("[title]", this.itemTitle);
      this.hideError();

      if (this.isDrawer) {
        this.fitAboveFooter();
      } else {
        document.documentElement.classList.add("prevent-scroll");
      }

      this.classList.add("is-open");
      this.panel.focus({ preventScroll: true });
    }

    close() {
      if (!this.isOpen) return;

      this.classList.remove("is-open");
      if (!this.isDrawer) document.documentElement.classList.remove("prevent-scroll");
      if (this.trigger && this.trigger.isConnected) this.trigger.focus({ preventScroll: true });
    }

    // No drawer o painel sobe da borda de cima do rodape. Sem altura para ele ali
    // (tela baixa), passa a cobrir o rodape.
    fitAboveFooter() {
      const inner = this.closest(".m-cart-drawer__inner");
      const footer = this.parentElement;
      if (!inner || !footer) return;

      const gap = 16;
      const minSpace = 240;
      const space = footer.getBoundingClientRect().top - inner.getBoundingClientRect().top - gap;
      const overFooter = space < minSpace;
      const maxHeight = overFooter ? inner.clientHeight - gap : space;

      this.classList.toggle("m-gift-wrap-sheet--over-footer", overFooter);
      this.style.setProperty("--m-gift-wrap-sheet-max-height", `${Math.round(maxHeight)}px`);
    }

    onClick(event) {
      if (event.target.closest("[data-gift-wrap-close]")) {
        event.preventDefault();
        this.close();
        return;
      }

      const addButton = event.target.closest(".m-gift-wrap-sheet__add");
      if (addButton && !addButton.disabled) {
        event.preventDefault();
        this.add(addButton);
      }
    }

    onKeydown(event) {
      if (!this.isOpen) return;

      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        this.close();
        return;
      }

      if (event.key === "Tab") this.keepFocusInside(event);
    }

    keepFocusInside(event) {
      const focusable = Array.from(this.panel.querySelectorAll("button:not([disabled])"));
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || active === this.panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    async add(button) {
      if (this.busy) return;
      this.busy = true;
      this.hideError();
      button.classList.add("m-spinner-loading");

      const { routes } = window.MinimogSettings;
      const parentVariantId = String(this.variantId);
      const properties = { _gift_wrap_for: parentVariantId };
      if (this.dataset.propertyName && this.itemTitle) properties[this.dataset.propertyName] = this.itemTitle;

      const body = JSON.stringify({
        items: [{ id: Number(button.dataset.variantId), quantity: 1, properties }],
        sections: getGiftWrapSectionsToBundle(),
        sections_url: window.location.pathname,
      });

      try {
        const res = await fetch(routes.cart_add_url, { ...fetchConfig(), body });
        const response = await res.json();

        // 422: sem estoque (ou outra regra da Shopify). A opcao sai de uso aqui.
        // O erro vem como { status, description } ou como { errors }.
        if (!res.ok || response.status || response.errors) {
          if (res.status === 422) button.disabled = true;
          const errors = typeof response.errors === "string" ? response.errors : "";
          this.showError(response.description || errors || response.message);
          return;
        }

        if (response.sections) {
          const cart = await fetch(`${window.Shopify.routes.root}cart.js`).then((res) => res.json());
          cart.sections = response.sections;
          emitGiftWrapCartUpdate(cart);
        } else {
          document.dispatchEvent(new CustomEvent("cart:refresh"));
        }

        // O carrinho pode ser redesenhado de novo logo depois (brindes da barra de
        // promocao); se o foco cair no body, volta para o bloco da embalagem.
        const focusWrapBlock = () => {
          if (document.activeElement && document.activeElement !== document.body) return;
          const wrapBlock = document.querySelector(`[data-gift-wrap-parent="${parentVariantId}"]`);
          if (wrapBlock) wrapBlock.focus({ preventScroll: true });
        };
        requestAnimationFrame(focusWrapBlock);
        setTimeout(focusWrapBlock, 1000);
      } catch (error) {
        console.error(error);
        this.showError();
      } finally {
        this.busy = false;
        button.classList.remove("m-spinner-loading");
      }
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || this.dataset.errorMessage;
      this.errorEl.hidden = false;
    }

    hideError() {
      if (!this.errorEl) return;
      this.errorEl.textContent = "";
      this.errorEl.hidden = true;
    }
  }
  customElements.define("m-gift-wrap-sheet", MGiftWrapSheet);
}

if (!customElements.get("m-gift-wrap-sync")) {
  class MGiftWrapSync extends HTMLElement {
    // O mesmo ajuste nao e repetido por alguns segundos: evita laco quando a
    // Shopify recusa a mudanca e o carrinho volta igual.
    static lastSignature = "";
    static lastSyncAt = 0;

    connectedCallback() {
      const signature = this.dataset.updates;
      if (!signature) return;

      const now = Date.now();
      if (signature === MGiftWrapSync.lastSignature && now - MGiftWrapSync.lastSyncAt < 5000) return;
      MGiftWrapSync.lastSignature = signature;
      MGiftWrapSync.lastSyncAt = now;

      let updates;
      try {
        updates = JSON.parse(signature);
      } catch (error) {
        return;
      }
      this.sync(updates);
    }

    async sync(updates) {
      const body = JSON.stringify({
        updates,
        sections: getGiftWrapSectionsToBundle(),
        sections_url: window.location.pathname,
      });

      try {
        const res = await fetch(window.MinimogSettings.routes.cart_update_url, { ...fetchConfig(), body });
        const cart = await res.json();
        if (!res.ok || !cart.items) return;
        if (!cart.sections) {
          document.dispatchEvent(new CustomEvent("cart:refresh"));
          return;
        }
        emitGiftWrapCartUpdate(cart);
      } catch (error) {
        console.error(error);
      }
    }
  }
  customElements.define("m-gift-wrap-sync", MGiftWrapSync);
}
